import { Portfolio, FamilyMember, InvestmentFolio } from '../types';
import { apiService } from '../services/api';

// Helper function to get ID from either _id or id field
const getId = (item: { _id?: string; id?: string }): string => {
  return item._id || item.id || '';
};

// Helper function to get family member ID from investment
const getFamilyMemberId = (investment: InvestmentFolio): string => {
  if (typeof investment.familyMemberId === 'string') {
    return investment.familyMemberId;
  }
  return getId(investment.familyMemberId as FamilyMember);
};

export const loadPortfolio = async (): Promise<Portfolio> => {
  try {
    const [familyMembers, investments, expenses, incomes] = await Promise.all([
      apiService.getFamilyMembers(),
      apiService.getInvestments(),
      apiService.getExpenses(),
      apiService.getIncomes(),
    ]);
    
    return {
      familyMembers,
      investments,
      expenses,
      incomes,
    };
  } catch (error) {
    console.error('Error loading portfolio from API:', error);
    // Return empty portfolio if API fails
    return {
      familyMembers: [],
      investments: [],
      expenses: [],
      incomes: [],
    };
  }
};

export const addFamilyMember = async (portfolio: Portfolio, member: FamilyMember): Promise<Portfolio> => {
  try {
    const newMember = await apiService.createFamilyMember({
      name: member.name,
      relationship: member.relationship,
      dateOfBirth: member.dateOfBirth,
    });
    
    return {
      ...portfolio,
      familyMembers: [...portfolio.familyMembers, newMember],
    };
  } catch (error) {
    console.error('Error adding family member:', error);
    throw error;
  }
};

export const updateFamilyMember = async (portfolio: Portfolio, updatedMember: FamilyMember): Promise<Portfolio> => {
  try {
    const memberId = getId(updatedMember);
    const updated = await apiService.updateFamilyMember(memberId, updatedMember);
    
    return {
      ...portfolio,
      familyMembers: portfolio.familyMembers.map(member =>
        getId(member) === memberId ? updated : member
      ),
    };
  } catch (error) {
    console.error('Error updating family member:', error);
    throw error;
  }
};

export const deleteFamilyMember = async (portfolio: Portfolio, memberId: string): Promise<Portfolio> => {
  try {
    await apiService.deleteFamilyMember(memberId);
    
    return {
      ...portfolio,
      familyMembers: portfolio.familyMembers.filter(member => getId(member) !== memberId),
      investments: portfolio.investments.filter(investment => getFamilyMemberId(investment) !== memberId),
    };
  } catch (error) {
    console.error('Error deleting family member:', error);
    throw error;
  }
};

export const addInvestment = async (portfolio: Portfolio, investment: InvestmentFolio): Promise<Portfolio> => {
  try {
    const newInvestment = await apiService.createInvestment({
      familyMemberId: getFamilyMemberId(investment),
      type: investment.type,
      name: investment.name,
      description: investment.description,
      investedAmount: investment.investedAmount,
      currentValue: investment.currentValue,
      units: investment.units,
      purchaseDate: investment.purchaseDate,
      maturityDate: investment.maturityDate,
      interestRate: investment.interestRate,
    });
    
    return {
      ...portfolio,
      investments: [...portfolio.investments, newInvestment],
    };
  } catch (error) {
    console.error('Error adding investment:', error);
    throw error;
  }
};

export const updateInvestment = async (portfolio: Portfolio, updatedInvestment: InvestmentFolio): Promise<Portfolio> => {
  try {
    const investmentId = getId(updatedInvestment);
    const updated = await apiService.updateInvestment(investmentId, {
      familyMemberId: getFamilyMemberId(updatedInvestment),
      type: updatedInvestment.type,
      name: updatedInvestment.name,
      description: updatedInvestment.description,
      investedAmount: updatedInvestment.investedAmount,
      currentValue: updatedInvestment.currentValue,
      units: updatedInvestment.units,
      purchaseDate: updatedInvestment.purchaseDate,
      maturityDate: updatedInvestment.maturityDate,
      interestRate: updatedInvestment.interestRate,
    });
    
    return {
      ...portfolio,
      investments: portfolio.investments.map(investment =>
        getId(investment) === investmentId ? updated : investment
      ),
    };
  } catch (error) {
    console.error('Error updating investment:', error);
    throw error;
  }
};

export const deleteInvestment = async (portfolio: Portfolio, investmentId: string): Promise<Portfolio> => {
  try {
    await apiService.deleteInvestment(investmentId);
    
    return {
      ...portfolio,
      investments: portfolio.investments.filter(investment => getId(investment) !== investmentId),
    };
  } catch (error) {
    console.error('Error deleting investment:', error);
    throw error;
  }
};

export const transferInvestment = async (
  portfolio: Portfolio,
  investmentId: string,
  newFamilyMemberId: string
): Promise<Portfolio> => {
  try {
    const updated = await apiService.transferInvestment(investmentId, newFamilyMemberId);
    
    return {
      ...portfolio,
      investments: portfolio.investments.map(investment =>
        getId(investment) === investmentId ? updated : investment
      ),
    };
  } catch (error) {
    console.error('Error transferring investment:', error);
    throw error;
  }
};

// Migration function to move data from localStorage to MongoDB
export const migrateFromLocalStorage = async (): Promise<void> => {
  try {
    const localData = localStorage.getItem('financial-planner-data');
    if (localData) {
      const portfolio: Portfolio = JSON.parse(localData);
      await apiService.migrateData(portfolio);
      
      // Clear localStorage after successful migration
      localStorage.removeItem('financial-planner-data');
      console.log('✅ Data migrated successfully from localStorage to MongoDB');
    }
  } catch (error) {
    console.error('❌ Error migrating data:', error);
    throw error;
  }
};

// Check if MongoDB is available
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    await apiService.healthCheck();
    return true;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};
